import { useMemo } from "react";
import { Link } from "react-router-dom";
import { LEAGUE } from "../../config/league";
import type { LeaderboardDTO, LeaderboardEntry, MoversDTO } from "../../shared/types";
import { HeroBanner } from "../components/HeroBanner";
import { LeagueHeatmap } from "../components/LeagueHeatmap";
import { ReturnChart, lastReturn } from "../components/ReturnChart";
import { SectorBattle } from "../components/SectorBattle";
import { SocialLinks } from "../components/SocialLinks";
import { Sparkline } from "../components/Sparkline";
import { TickerTape } from "../components/TickerTape";
import {
  DeltaArrow,
  ErrorBanner,
  LivePill,
  RankBadge,
  ReturnValue,
  SectionHeader,
  Skeleton,
  StatTile,
} from "../components/ui";
import { api } from "../lib/api";
import { managerColor } from "../lib/colors";
import {
  formatPct,
  formatTimeFromISO,
  returnColor,
  useFlash,
  useFlipList,
  usePoll,
} from "../lib/format";

export default function SeasonTrackerPage() {
  const { data, error, loading } = usePoll(
    api.leaderboard,
    LEAGUE.leaderboardPollSec * 1000,
  );
  const { data: movers } = usePoll(api.movers, LEAGUE.leaderboardPollSec * 1000);
  const register = useFlipList<number>();

  return (
    <div className="space-y-6">
      <TickerTape data={movers} />
      <HeroBanner />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-fg">
            {LEAGUE.seasonName} · <span className="text-brand">Standings</span>
          </h1>
          <p className="mt-1 text-sm text-muted">
            Equal-weight average return across {LEAGUE.positionsPerManager}{" "}
            positions per manager.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {data && <LivePill live={data.marketOpen} />}
          {data && (
            <span className="num text-xs text-muted">
              updated {formatTimeFromISO(data.updatedAt)} ET
            </span>
          )}
          <Link
            to="/broadcast"
            className="rounded-lg border border-edge px-3 py-1.5 text-xs font-semibold text-muted transition hover:border-accent/60 hover:text-fg"
          >
            ⛶ Broadcast
          </Link>
        </div>
      </div>

      {error && <ErrorBanner message={error} />}

      {loading && !data ? (
        <LoadingState />
      ) : (
        data && (
          <>
            <StatStrip data={data} />
            <div className="space-y-2.5">
              {data.entries.map((e) => (
                <LeaderRow key={e.id} e={e} innerRef={register(e.id)} />
              ))}
            </div>
            <LeagueHeatmap entries={data.entries} />
            <SectorBattle data={movers} />
            <SeasonRace />
            <Movers data={movers} />
          </>
        )
      )}
    </div>
  );
}

function SeasonRace() {
  const { data } = usePoll(api.history, LEAGUE.leaderboardPollSec * 1000);
  const series = useMemo(
    () =>
      (data?.managers ?? [])
        .map((m) => ({
          name: m.name,
          color: managerColor(m.seed),
          points: m.points,
        }))
        .sort((a, b) => lastReturn(b) - lastReturn(a)), // leader first
    [data],
  );
  return (
    <div className="panel p-4">
      <SectionHeader
        title="Season race"
        right={
          <Link
            to="/broadcast/race"
            className="rounded-lg border border-edge px-3 py-1.5 text-xs font-semibold text-muted transition hover:border-accent/60 hover:text-fg"
          >
            ⛶ Broadcast
          </Link>
        }
      />
      <ReturnChart series={series} height={260} xAxis />
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
        {series.map((s) => (
          <span key={s.name} className="flex items-center gap-1.5 text-xs text-muted">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color }} />
            {s.name}
          </span>
        ))}
      </div>
    </div>
  );
}

function Movers({ data }: { data: MoversDTO | null }) {
  const positions = data?.positions ?? [];
  const live = positions.filter((p) => p.return !== 0);
  if (live.length === 0) return null;
  const gainers = live.filter((p) => p.return > 0).slice(0, 5);
  const losers = live
    .filter((p) => p.return < 0)
    .slice(-5)
    .reverse();

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <MoverList title="Top gainers" items={gainers} />
      <MoverList title="Biggest losers" items={losers} />
    </div>
  );
}

function MoverList({
  title,
  items,
}: {
  title: string;
  items: { ticker: string; direction: string; manager: string; seed: number; return: number }[];
}) {
  return (
    <div className="panel p-4">
      <SectionHeader title={title} />
      {items.length === 0 ? (
        <p className="text-sm text-faint">None yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((p) => (
            <li
              key={`${p.ticker}-${p.direction}-${p.manager}`}
              className="flex items-center gap-2 text-sm"
            >
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ background: managerColor(p.seed) }}
              />
              <span className="num w-16 shrink-0 font-bold text-fg">{p.ticker}</span>
              <span className="hidden text-[10px] font-bold text-faint sm:inline">
                {p.direction}
              </span>
              <span className="min-w-0 flex-1 truncate text-muted">{p.manager}</span>
              <span className={`num font-bold ${returnColor(p.return)}`}>
                {formatPct(p.return)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatStrip({ data }: { data: LeaderboardDTO }) {
  const entries = data.entries;
  if (entries.length === 0) return null;
  const avgs = entries.map((e) => e.avgReturn);
  const leagueAvg = avgs.reduce((a, b) => a + b, 0) / avgs.length;
  const leader = entries[0];
  const last = entries[entries.length - 1];
  const spread = leader.avgReturn - last.avgReturn;
  const green = entries.filter((e) => e.avgReturn > 0).length;

  let best = { val: -Infinity, name: "" };
  let worst = { val: Infinity, name: "" };
  for (const e of entries) {
    for (const r of e.slotReturns) {
      if (r > best.val) best = { val: r, name: e.name };
      if (r < worst.val) worst = { val: r, name: e.name };
    }
  }

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      <StatTile
        label="League avg"
        value={formatPct(leagueAvg)}
        tone={returnColor(leagueAvg)}
        sub="mean of all managers"
      />
      <StatTile
        label="Leader"
        value={leader.name}
        sub={formatPct(leader.avgReturn)}
        tone="text-gold"
      />
      <StatTile label="1st → last spread" value={formatPct(spread, 1)} sub="gap to bottom" />
      <StatTile
        label="Best position"
        value={formatPct(best.val)}
        tone="text-gain"
        sub={best.name}
      />
      <StatTile
        label="In the green"
        value={`${green}/${entries.length}`}
        sub={`worst ${formatPct(worst.val)} · ${worst.name}`}
      />
    </div>
  );
}

function LeaderRow({
  e,
  innerRef,
}: {
  e: LeaderboardEntry;
  innerRef: (el: HTMLElement | null) => void;
}) {
  const top = e.rank === 1;
  const flash = useFlash(e.avgReturn);
  return (
    <div
      ref={innerRef as never}
      className={`card-link panel relative flex items-center gap-4 px-4 py-4 ${
        top ? "ring-1 ring-gold/40" : ""
      }`}
    >
      {/* Stretched link makes the whole card navigate, while the social icons
          above it stay independently clickable (no nested <a>). */}
      <Link
        to={`/manager/${encodeURIComponent(e.name)}`}
        aria-label={`${e.name} details`}
        className="absolute inset-0 z-0 rounded-2xl"
      />
      <div className="flex w-12 shrink-0 flex-col items-center gap-1">
        <RankBadge rank={e.rank} />
        <DeltaArrow delta={e.delta} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2.5">
          <span className="truncate text-xl font-bold text-fg">{e.name}</span>
          <span className="num hidden text-xs text-faint sm:inline">
            seed #{e.seed}
          </span>
          <SocialLinks name={e.name} className="relative z-10" />
        </div>
        <div className="mt-1 hidden flex-wrap gap-1 sm:flex">
          {e.slotReturns.map((r, i) => (
            <span
              key={i}
              title={`Slot ${i + 1}: ${formatPct(r)}`}
              className={`h-1.5 w-6 rounded-full ${
                r > 0 ? "bg-gain/70" : r < 0 ? "bg-loss/70" : "bg-ink-600"
              }`}
            />
          ))}
        </div>
      </div>

      <div className="hidden w-40 shrink-0 sm:block">
        <Sparkline data={e.sparkline} height={40} />
      </div>

      <div className={`w-24 shrink-0 rounded text-right sm:w-32 ${flash}`}>
        <ReturnValue
          value={e.avgReturn}
          className={`text-2xl sm:text-3xl ${top ? "glow-gain" : ""}`}
          showArrow
        />
        <div className={`text-[11px] ${returnColor(e.avgReturn)}`}>avg return</div>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-[68px]" />
        ))}
      </div>
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-[84px]" />
      ))}
    </div>
  );
}
